import React, { useRef, useEffect, useMemo, useState, useCallback } from "react";
import { isValidRule } from "components/modules/policy-editor/utils/ruleValidator";
import { TableRow, TableCell, Typography, IconButton, Tooltip, TextField } from "@mui/material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IniInfo } from "system/types/interfaces";
import { ConfirmationDialog } from "components/ui";
import { Icon } from "@fluentui/react/lib/Icon";
import { DeleteRegular, EditRegular } from "@fluentui/react-icons";

interface ActiveDirectoryDomainRowProps {
  id: string;
  domain: IniInfo;
  index: number;
  style?: React.CSSProperties;
  onAdd: (index: number) => void;
  onDelete: (index: number) => void;
  onUndoDelete: (index: number) => void;
  onEdit: (domain: IniInfo, index: number, isComment: boolean) => void;
  searchTerm: string;
  onReorder: (oldIndex: number, newIndex: number) => void;
  totalRows: number;
  onToggleComment: (id: string, newContent: string, isComment: boolean) => void;
  readOnlyViewer: boolean;
}

export const ActiveDirectoryDomainRow = React.memo(
  ({
    id,
    domain,
    index,
    style,
    onAdd,
    onDelete,
    onUndoDelete,
    onEdit,
    searchTerm,
    onReorder,
    totalRows,
    onToggleComment,
    readOnlyViewer,
  }: ActiveDirectoryDomainRowProps) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
    const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);
    const [isEditingRowNumber, setIsEditingRowNumber] = useState(false);
    const [rowNumberInput, setRowNumberInput] = useState<string>((index + 1).toString());

    const nameRef = useRef<HTMLDivElement>(null);
    const valueRef = useRef<HTMLDivElement>(null);
    const commentRef = useRef<HTMLDivElement>(null);

    const [isNameOverflowing, setIsNameOverflowing] = useState(false);
    const [isValueOverflowing, setIsValueOverflowing] = useState(false);
    const [isCommentOverflowing, setIsCommentOverflowing] = useState(false);

    const isComment = domain.isComment ?? false;
    const isDeleted = domain.isDeleted || false;
    const isModified = domain.pendingChanges;
    const isDefault = domain.name === '""';

    const rowStyle: React.CSSProperties = {
      ...style,
      transform: CSS.Transform.toString(transform),
      transition,
      display: "grid",
      gridTemplateColumns: isComment ? "5% 5% 65% 0% 25%" : "5% 5% 30% 35% 25%",
      alignItems: "center",
      borderBottom: "0.5px solid #515151",
      placeContent: "center",
      backgroundColor: isComment ? "#1e1e1e" : isDeleted ? "#2a2a2a" : isDefault ? "#333" : "inherit",
      height: "50px",
      overflow: "hidden",
    };

    const escapeRegExp = (string: string) => string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const highlightText = useCallback(
      (text?: string) => {
        if (!searchTerm || !text) return text;
        try {
          const escapedSearchTerm = escapeRegExp(searchTerm);
          const parts = text.split(new RegExp(`(${escapedSearchTerm})`, "gi"));
          return parts.map((part, i) =>
            part.toLowerCase() === searchTerm.toLowerCase() ? (
              <span key={i} style={{ backgroundColor: "yellow", color: "black" }}>
                {part}
              </span>
            ) : (
              part
            )
          );
        } catch (error) {
          return text;
        }
      },
      [searchTerm]
    );

    const scrollCellStyles = {
      "&::-webkit-scrollbar": { height: "4px" },
      "&::-webkit-scrollbar-thumb": { backgroundColor: "#555", borderRadius: "4px" },
      "&::-webkit-scrollbar-thumb:hover": { height: "8px", backgroundColor: "#ccc" },
    };

    const tooltipStyles = {
      backgroundColor: "#333",
      color: "#fff",
      fontSize: "12px",
      padding: "8px",
      borderRadius: "4px",
      maxWidth: "500px",
      whiteSpace: "normal",
      wordBreak: "break-word",
    };

    useEffect(() => {
      const checkOverflow = (ref: React.RefObject<HTMLDivElement>, setOverflow: (overflow: boolean) => void) => {
        if (ref.current) {
          const { scrollWidth, clientWidth } = ref.current;
          setOverflow(scrollWidth > clientWidth);
        }
      };

      if (isComment) {
        checkOverflow(commentRef, setIsCommentOverflowing);
      } else {
        checkOverflow(nameRef, setIsNameOverflowing);
        checkOverflow(valueRef, setIsValueOverflowing);
      }
    }, [isComment, domain.name, domain.delimiter, domain.value, domain.inlineComment]);

    const handleRowNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowNumberInput(event.target.value);
    };

    const handleRowNumberConfirm = () => {
      const newRowNumber = parseInt(rowNumberInput, 10);
      const currentRowNumber = index + 1;

      if (isNaN(newRowNumber) || newRowNumber < 1 || newRowNumber > totalRows) {
        setRowNumberInput(currentRowNumber.toString());
      } else if (newRowNumber !== currentRowNumber) {
        const oldIndex = index;
        const newIndex = newRowNumber - 1;
        onReorder(oldIndex, newIndex);
      }

      setIsEditingRowNumber(false);
    };

    const handleRowNumberClick = () => {
      if (readOnlyViewer || isDefault) return;
      setIsEditingRowNumber(true);
    };

    const handleRowNumberKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.stopPropagation();
        handleRowNumberConfirm();
      }
    };

    const canUncomment = useMemo(() => {
      if (!isComment) return true;
      // Domain Name is optional in this section ("leave blank to skip the domain join").
      return isValidRule(domain.value ?? "", { allowEmptyName: true });
    }, [isComment, domain.value]);

    const handleToggleComment = () => {
      if (isDefault) return;
      if (isComment) {
        if (!canUncomment) return;
        onToggleComment(id, "", false);
      } else {
        const currentContent = `${domain.name} ${domain.delimiter} ${domain.value} ${
          domain.inlineComment ? ` ;${domain.inlineComment}` : ""
        }`;
        const commentedContent = `; ${currentContent.trim()}`;
        onToggleComment(id, commentedContent, true);
      }
    };

    const isImmutable = isDefault;

    return (
      <>
        <TableRow
          id={`row-${index}`}
          component="div"
          ref={setNodeRef}
          style={rowStyle}
          sx={{
            "&:hover": {
              backgroundColor: isComment ? "#2a2a2a" : isDeleted ? "#2a2a2a" : isDefault ? "#333" : "#333",
            },
            height: "auto",
          }}
        >
          {/* Drag */}
          <TableCell
            component="div"
            {...listeners}
            {...attributes}
            sx={{
              cursor: isImmutable ? "default" : "grab",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderStyle: "none",
              visibility: readOnlyViewer || isImmutable ? "hidden" : "visible",
            }}
          >
            <Tooltip title="Drag to reorder" arrow>
              <span><Icon iconName="GripperBarHorizontal" style={{ color: isComment ? "#4caf50" : "#757575" }} /></span>
            </Tooltip>
          </TableCell>

          {/* Sequence */}
          <TableCell
            component="div"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderStyle: "none",
              cursor: isImmutable ? "default" : "pointer",
            }}
          >
            {isEditingRowNumber ? (
              <TextField
                value={rowNumberInput}
                onChange={handleRowNumberChange}
                onBlur={handleRowNumberConfirm}
                onKeyDown={handleRowNumberKeyDown}
                autoFocus
                variant="standard"
                type="number"
                sx={{
                  fontSize: "16px",
                  width: "40px",
                  "& .MuiInput-underline:before": { borderBottomColor: "#757575" },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottomColor: "#fff" },
                  "& .MuiInput-underline:after": { fontSize: "16px", borderBottomColor: "#4caf50" },
                }}
                inputProps={{ min: 1, max: totalRows, style: { padding: 0, fontSize: "12px" } }}
              />
            ) : (
              <Typography
                onClick={handleRowNumberClick}
                sx={{
                  color: "#fff",
                  fontSize: "14px",
                  border: "1px solid #6b6b6b",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  "&:hover": { color: "#4caf50" },
                  opacity: isImmutable ? 0.5 : 1,
                }}
              >
                {index + 1}
              </Typography>
            )}
          </TableCell>

          {/* Content */}
          {isComment ? (
            <>
              <TableCell
                ref={commentRef}
                component="div"
                sx={{
                  overflowY: "hidden",
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  borderStyle: "none",
                  lineHeight: 1.5,
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                  ...scrollCellStyles,
                }}
              >
                <Tooltip title={domain.value || ""} disableHoverListener={!isCommentOverflowing} sx={tooltipStyles}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: isDeleted ? "#d5878b" : "#4caf50",
                      fontStyle: "italic",
                      textDecoration: isDeleted ? "line-through" : "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {highlightText(domain.value || "")}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell component="div" sx={{ overflowX: "auto", whiteSpace: "nowrap", borderStyle: "none", lineHeight: 1.5 }}></TableCell>
            </>
          ) : (
            <>
              <TableCell
                component="div"
                ref={nameRef}
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  whiteSpace: "nowrap",
                  borderStyle: "none",
                  lineHeight: 1.5,
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                  ...scrollCellStyles,
                }}
              >
                <Tooltip title={domain.name || ""} disableHoverListener={!isNameOverflowing} sx={tooltipStyles}>
                  <Typography
                    variant="body1"
                    sx={{
                      textDecoration: isDeleted ? "line-through" : "none",
                      color: isDeleted ? "#d5878b" : isDefault ? "#aaa" : "#fff",
                      opacity: isImmutable ? 0.5 : 1,
                    }}
                  >
                    {domain.name.length > 0 ? (
                      highlightText(domain.name)
                    ) : (
                      <span style={{ opacity: 0.4 }}>skip the domain join</span>
                    )}
                  </Typography>
                </Tooltip>
              </TableCell>

              <TableCell
                component="div"
                ref={valueRef}
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  whiteSpace: "nowrap",
                  borderStyle: "none",
                  lineHeight: 1.5,
                  height: "50px",
                  display: "flex",
                  alignItems: "center",
                  ...scrollCellStyles,
                }}
              >
                <Tooltip
                  title={`${domain.delimiter} ${domain.value}${domain.inlineComment ? `;${domain.inlineComment}` : ""}`}
                  disableHoverListener={!isValueOverflowing}
                  sx={tooltipStyles}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      textDecoration: isDeleted ? "line-through" : "none",
                      color: isDeleted ? "#d5878b" : "#fff",
                      opacity: isImmutable ? 0.5 : 1,
                    }}
                  >
                    {highlightText(domain.delimiter)} {highlightText(domain.value)}
                    {domain.inlineComment && (
                      <Typography variant="caption" style={{ color: "#4caf50", fontStyle: "italic" }}>
                        {";" + highlightText(domain.inlineComment)}
                      </Typography>
                    )}
                  </Typography>
                </Tooltip>
              </TableCell>
            </>
          )}

          {/* Actions */}
          <TableCell
            component="div"
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "8px",
              borderStyle: "none",
              justifySelf: "flex-end",
              height: "50px",
            }}
          >
            {isModified && (
              <Typography
                variant="body2"
                sx={{
                  backgroundColor: isDeleted ? "#d5878b" : "#fbc02d",
                  color: "#000",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  fontWeight: "bold",
                }}
              >
                {isDeleted ? "Deleted Row" : "Pending Changes"}
              </Typography>
            )}

            <Tooltip
              title={
                isComment
                  ? canUncomment
                    ? "Uncomment Rule"
                    : "Cannot uncomment: the comment does not contain a valid rule. Edit or delete it instead."
                  : "Comment Rule"
              }
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleToggleComment}
                  hidden={readOnlyViewer || isImmutable}
                  disabled={isComment && !canUncomment}
                  sx={{
                    color: isComment ? (canUncomment ? "#4caf50" : "#8a8886") : "#8a8886",
                    "&:hover": { color: isComment ? (canUncomment ? "#66bb6a" : "#8a8886") : "#bdbdbd" },
                  }}
                >
                  {isComment ? <Icon iconName="CommentAdd" style={{ fontSize: "small" }} /> : <Icon iconName="Comment" style={{ fontSize: "small" }} />}
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Add line">
              <IconButton size="small" color="primary" onClick={() => onAdd(index)} hidden={readOnlyViewer}>
                <Icon iconName="Add" />
              </IconButton>
            </Tooltip>

            {isDeleted ? (
              <Tooltip title="Undo Delete" arrow>
                <IconButton size="small" onClick={() => onUndoDelete(index)} hidden={readOnlyViewer}>
                  <Icon iconName="Undo" />
                </IconButton>
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Delete" arrow>
                  <IconButton
                    size="small"
                    color="error"
                    hidden={readOnlyViewer || isImmutable}
                    sx={{ color: "#d54c52" }}
                    onClick={() => setConfirmationDialogVisible(true)}
                  >
                    <DeleteRegular fontSize={22} style={{ color: "#d5878b" }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={isComment ? "Edit Comment" : "Edit Domain"} arrow>
                  <IconButton
                    size="small"
                    sx={{ color: "#8a8886" }}
                    hidden={readOnlyViewer || isImmutable}
                    onClick={() => onEdit(domain, index, isComment)}
                  >
                    <EditRegular fontSize={22} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </TableCell>
        </TableRow>


        <ConfirmationDialog
          title="Delete Domain"
          subText={`Are you sure you want to delete this domain?`}
          isVisible={confirmationDialogVisible}
          onConfirm={() => {
            setConfirmationDialogVisible(false);
            onDelete(index);
          }}
          onCancel={() => {
            setConfirmationDialogVisible(false);
          }}
        />
      </>
    );
  }
);
